import React, { useEffect, useState } from 'react';
import type { AIAgentWebhook, ToolFilterCondition } from '@troia-v3/types';

import { Button, Drawer, Input, Select, Switch, Textarea } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { useCreateAgentWebhook, useUpdateAgentWebhook } from '../hooks/useAgentWebhooks';

import { WebhookFilterBuilder } from './filters/WebhookFilterBuilder';

export interface WebhookFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  agentId: string;
  webhook?: AIAgentWebhook | null;
}

type WebhookFormTab = 'webhook' | 'ai' | 'filters';

interface WebhookFormState {
  name: string;
  description: string;
  method: 'POST' | 'PUT' | 'PATCH';
  aiEnabled: boolean;
  aiPrompt: string;
  filters: ToolFilterCondition[];
}

const EMPTY_FORM: WebhookFormState = {
  name: '',
  description: '',
  method: 'POST',
  aiEnabled: false,
  aiPrompt: '',
  filters: [],
};

const METHOD_OPTIONS = [
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
];

const TABS: Array<{ id: WebhookFormTab; label: string; icon: 'link' | 'cpu' | 'filter' }> = [
  { id: 'webhook', label: 'Webhook', icon: 'link' },
  { id: 'ai', label: 'Processamento com IA', icon: 'cpu' },
  { id: 'filters', label: 'Filtros', icon: 'filter' },
];

/**
 * Resolve o estado do form a partir de um webhook existente, com fallback
 * legado: webhooks antigos têm `prompt` na raiz (IA sempre ativa).
 */
function formStateFromWebhook(webhook: AIAgentWebhook): WebhookFormState {
  const aiConfig = webhook.aiProcessing
    ?? (webhook.prompt ? { enabled: true, prompt: webhook.prompt } : { enabled: false, prompt: '' });
  return {
    name: webhook.name,
    description: webhook.description || '',
    method: webhook.method,
    aiEnabled: aiConfig.enabled,
    aiPrompt: aiConfig.prompt,
    filters: webhook.filters || [],
  };
}

export const WebhookFormDrawer: React.FC<WebhookFormDrawerProps> = ({
  isOpen,
  onClose,
  mode,
  agentId,
  webhook,
}) => {
  const isEdit = mode === 'edit';
  const [activeTab, setActiveTab] = useState<WebhookFormTab>('webhook');
  const [formData, setFormData] = useState<WebhookFormState>(EMPTY_FORM);

  const createWebhook = useCreateAgentWebhook(agentId);
  const updateWebhook = useUpdateAgentWebhook(agentId);
  const isLoading = createWebhook.isPending || updateWebhook.isPending;

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab('webhook');
    setFormData(isEdit && webhook ? formStateFromWebhook(webhook) : EMPTY_FORM);
  }, [isOpen, isEdit, webhook]);

  const promptTooShort = formData.aiEnabled && formData.aiPrompt.trim().length < 10;
  const canSubmit =
    formData.name.trim().length > 0 &&
    !promptTooShort &&
    formData.filters.every((f) => f.field.trim().length > 0);

  // Abas do header do Drawer (F76) — labels em ReactNode preservam os badges
  // (pill "Ativo" quando a IA está ligada, contador roxo de filtros).
  const tabItems = TABS.map((tab) => ({
    id: tab.id,
    icon: tab.icon,
    label: (
      <span className="inline-flex items-center">
        {tab.label}
        {tab.id === 'ai' && formData.aiEnabled && (
          <span
            className="ml-1 px-1.5 py-0.5 text-xs rounded-full"
            style={{ backgroundColor: '#dcfce7', color: '#15803d' }}
          >
            Ativo
          </span>
        )}
        {tab.id === 'filters' && formData.filters.length > 0 && (
          <span
            className="ml-1 px-1.5 py-0.5 text-xs rounded-full"
            style={{ backgroundColor: '#f3e8ff', color: '#8b5cf6' }}
          >
            {formData.filters.length}
          </span>
        )}
      </span>
    ),
  }));

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      method: formData.method,
      // Sempre enviar aiProcessing/filters — garante que desativar/limpar persiste
      aiProcessing: { enabled: formData.aiEnabled, prompt: formData.aiPrompt.trim() },
      filters: formData.filters,
    };

    try {
      if (isEdit && webhook) {
        await updateWebhook.mutateAsync({ webhookId: webhook.id, data: payload });
      } else {
        await createWebhook.mutateAsync(payload);
      }
      onClose();
    } catch {
      // Toast de erro já é emitido pelos hooks (onError)
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      icon={<Icon name={isEdit ? 'edit' : 'link'} className="w-5 h-5" />}
      title={isEdit ? 'Editar Webhook' : 'Novo Webhook'}
      tabs={{
        items: tabItems,
        active: activeTab,
        onChange: (id) => setActiveTab(id as WebhookFormTab),
      }}
      disabled={isLoading}
      footer={
        <>
          <Button onClick={onClose} variant="ghost" size="md" disabled={isLoading} data-testid="webhook-form-cancel">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="primary"
            size="md"
            disabled={!canSubmit || isLoading}
            data-testid="webhook-form-submit"
          >
            {isLoading ? (isEdit ? 'Salvando...' : 'Criando...') : (isEdit ? 'Salvar' : 'Criar')}
          </Button>
        </>
      }
    >
      <div className="px-6 py-5" data-testid="webhook-form-drawer">
        <div className="p-6 space-y-4">
          {/* ── Aba: Webhook ── */}
          {activeTab === 'webhook' && (
            <>
              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                  Nome *
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Notificação CRM"
                  data-testid="webhook-form-name"
                />
              </div>

              <div>
                <label className="block text-xs mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                  Descrição (opcional)
                </label>
                <Input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição do webhook..."
                  data-testid="webhook-form-description"
                />
              </div>

              <Select
                label="Método HTTP"
                value={formData.method}
                onChange={(value) => setFormData({ ...formData, method: value as 'POST' | 'PUT' | 'PATCH' })}
                options={METHOD_OPTIONS}
                data-testid="webhook-form-method"
              />
            </>
          )}

          {/* ── Aba: Processamento com IA ── */}
          {activeTab === 'ai' && (
            <>
              <div
                className="p-4 rounded-lg border flex items-center justify-between gap-4"
                style={{ borderColor: 'var(--theme-card-border)' }}
              >
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                    Processamento com IA
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                    Quando habilitado, o JSON recebido será transformado pela IA antes de ser
                    injetado na conversa. Desabilitado, o JSON é injetado como recebido.
                  </p>
                </div>
                <div data-testid="webhook-form-ai-toggle">
                  <Switch
                    checked={formData.aiEnabled}
                    onChange={(checked) => setFormData({ ...formData, aiEnabled: checked })}
                  />
                </div>
              </div>

              {formData.aiEnabled && (
                <div>
                  <Textarea
                    label="Prompt de IA * (min. 10 caracteres)"
                    value={formData.aiPrompt}
                    onChange={(e) => setFormData({ ...formData, aiPrompt: e.target.value })}
                    placeholder="Descreva como a IA deve processar os dados recebidos via webhook..."
                    rows={6}
                    maxLength={10000}
                    data-testid="webhook-form-prompt"
                  />
                  <p
                    className="text-xs mt-1"
                    style={{ color: promptTooShort ? 'var(--theme-error-color)' : 'var(--theme-muted-text)' }}
                  >
                    {formData.aiPrompt.length}/10000 caracteres
                    {promptTooShort ? ' — mínimo de 10 caracteres' : ''}
                  </p>
                </div>
              )}
            </>
          )}

          {/* ── Aba: Filtros ── */}
          {activeTab === 'filters' && (
            <WebhookFilterBuilder
              filters={formData.filters}
              onChange={(filters) => setFormData({ ...formData, filters })}
              disabled={isLoading}
            />
          )}
        </div>
      </div>
    </Drawer>
  );
};

WebhookFormDrawer.displayName = 'WebhookFormDrawer';
